import {
  DeleteVideoTextStyled,
  InfoVideoTextStyled,
  RootStyled,
  ThumbnailsContainerStyled,
  UploadWidgetStyled,
  VideoPlayerStyled,
} from './upload-widget.styled';
import React, { useEffect, useRef, useState } from 'react';
import { Stack, Typography } from '@mui/material';

import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { IconSize } from '@constants';
import ImageUpload from '@atoms/ImageUpload';
import ThumbnailImageUploadViewer from '@atoms/ThumbnailImageUploadViewer';
import { UploadThmbnailVideo } from '@atoms/UploadThmbnailVideo';
import { VideoThumbnailViewer } from '@atoms/VideoThumbnailViewer';
import { fetchFileFromURL } from '@/lib/fileUtil';
import { useTranslation } from 'react-i18next';

const allowedFormats = ['video/mp4'];
interface VideoUploadProps {
  onVideoUpload: (file: File) => void;
  onVideoDelete: () => void;
  onThumbnailUpload: (file: File) => void;
  value?: File;
  urlVideoValue?: string;
  urlThumbnailValue?: string;
}
const maxSize = 250 * 1024 * 1024; // 250MB in bytes

const VideoUpload: React.FC<VideoUploadProps> = ({
  onVideoUpload,
  onThumbnailUpload,
  value,
  urlVideoValue,
  urlThumbnailValue,
  onVideoDelete,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedThumnail, setSelectedThumnail] = useState<string>();
  const [isUploadImageUsed, setIsUploadImageUsed] = useState<boolean>();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | undefined>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!value) return;
    setVideoUrl(URL.createObjectURL(value));
  }, [value]);
  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    setVideoDuration(video.duration);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];

    if (!file) return;
    if (file.size >= maxSize) return;
    if (!allowedFormats.includes(file.type)) return;
    const url = URL.createObjectURL(file);

    onVideoUpload(file);

    setVideoUrl(url);
  };

  const handleClick = async () => {
    // Create an input element to trigger file selection
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4';
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;
      if (file.size >= maxSize) return;
      if (!allowedFormats.includes(file.type)) return;

      onVideoUpload(file);

      setVideoUrl(URL.createObjectURL(file));
    };

    input.click();
  };

  const handleDelete = () => {
    setSelectedThumnail(undefined);
    setVideoUrl(null);
    setIsDragging(false);
    onVideoDelete();
  };

  const handleThumbnailUpload = () => {
    if (!selectedThumnail) return;
    if (selectedThumnail === urlThumbnailValue) return;
    fetchFileFromURL(selectedThumnail, 'job-actu', 'image/jpg')
      .then((data) => onThumbnailUpload(data))
      .catch((e) => console.error(e));
  };

  useEffect(() => {
    if (!selectedThumnail && urlThumbnailValue) {
      setSelectedThumnail(urlThumbnailValue);
      setIsUploadImageUsed(true);
    }
    handleThumbnailUpload();
  }, [selectedThumnail, urlThumbnailValue]);

  useEffect(() => {
    if (urlVideoValue && !videoUrl) setVideoUrl(urlVideoValue);
  }, [urlVideoValue]);

  return (
    <RootStyled>
      <UploadWidgetStyled
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={handleClick}
        data-qa={`video-upload`}
      >
        {videoUrl ? (
          <VideoPlayerStyled
            controls
            ref={videoRef}
            onLoadedMetadata={handleLoadedMetadata}
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </VideoPlayerStyled>
        ) : (
          <Stack alignContent={'center'} gap={2}>
            <ArrowUpTrayIcon height={IconSize.md} />
            <Typography variant="body1">
              {isDragging ? '... ' : t('DRAG_VIDEO_OR_IMPORT_IT')}
            </Typography>
          </Stack>
        )}
      </UploadWidgetStyled>

      <Stack py={4}>
        {videoUrl && videoDuration ? (
          <Stack gap={2}>
            <DeleteVideoTextStyled
              data-qa="delete-video"
              onClick={handleDelete}
            >
              {t('DELETE_VIDEO')}
            </DeleteVideoTextStyled>

            {videoUrl && (
              <ThumbnailsContainerStyled>
                <Typography variant="body1" gutterBottom>
                  {t('SELECT_COVER_TEXT')}
                </Typography>
                <Stack direction="row" gap={2}>
                  {urlVideoValue !== videoUrl && (
                    <>
                      <VideoThumbnailViewer
                        selectedThumbnailUrl={selectedThumnail}
                        videoUrl={videoUrl}
                        snapshotAtTime={0}
                        onSelect={(thumbnailValue) => {
                          setSelectedThumnail(thumbnailValue);
                          setIsUploadImageUsed(false);
                        }}
                      />
                      <VideoThumbnailViewer
                        selectedThumbnailUrl={selectedThumnail}
                        videoUrl={videoUrl}
                        snapshotAtTime={videoDuration / 2}
                        onSelect={(thumbnailValue) => {
                          setSelectedThumnail(thumbnailValue);
                          setIsUploadImageUsed(false);
                        }}
                      />
                    </>
                  )}

                  <ImageUpload
                    isSelected={isUploadImageUsed}
                    ComponentUpload={UploadThmbnailVideo}
                    urlValue={urlThumbnailValue}
                    ComponentView={(props) => (
                      <ThumbnailImageUploadViewer
                        {...props}
                        isSelected={!!isUploadImageUsed}
                      />
                    )}
                    onImageUpload={async (file) => {
                      setSelectedThumnail(URL.createObjectURL(file));
                      setIsUploadImageUsed(true);
                    }}
                  />
                </Stack>
              </ThumbnailsContainerStyled>
            )}
          </Stack>
        ) : (
          <InfoVideoTextStyled>
            {t('RECOMMENDED_RESOLUTION')}
          </InfoVideoTextStyled>
        )}
      </Stack>
    </RootStyled>
  );
};

export default VideoUpload;
